import {Types} from 'mongoose';
import {Employee} from '@/interfaces/employee.interface';
import {Institution} from '@/interfaces/institution.interface';
import {InstitutionTypesVue} from '@/enums/InstitutionTypes.enum';
import {AcquisitionListStatusEnum} from '@/enums/institutioner/acquisition/AcquisitionListStatus.enum';
import {InstitutionerApi} from '@/classes/api/institutioner.api.class';
import {EmployeeHandlerAPI} from '@/classes/api/employee-handler.api.class';
import {
  airDistanceKm, containsId, courseSlots, DayState, dayStates, matchingDays, MAX_RADIUS_KM, PickerMode, Reason, WORK_DAYS,
} from '@/components/institutions/institutionView/profileView/teacherPicker/teacherPicker.helper';

export type InstitutionPickerWho = 'all' | 'free' | 'chosen';

export type SchoolRow = {
  id: string;
  institution: Institution;
  name: string;
  customId: string;
  initials: string;
  typeText: string;
  city: string;
  distanceKm: number | null;
  matchDays: number[];
  chosen: boolean;
  /** Chosen in the application or (assign mode) already planned: shown regardless of the radius */
  pinned: boolean;
  inactive: boolean;
  score: number;
  reasons: Reason[];
  days: DayState[];
  status: Reason | null;
};

export const INSTITUTION_FIELDS = ['name', 'customId', 'type', 'address', 'availabilities', 'totalAmountPupils', 'acquisitionStatus', 'isActive',
  'employeesWanted', 'applicants'];

export const TEACHER_FIELDS = ['firstName', 'lastName', 'address', 'availabilities', 'maxDistance', 'maxTravelTime', 'hasCar', 'hasDriverLicense',
  'subjects', 'assignedInstitutions', 'plannedInstitutions', 'isApplicant', 'isTeacher', 'vanillaApplication'];

/** Ids per request when counting the assigned teachers (GET query string must stay short) */
const COUNT_CHUNK = 40;

export function initialsOf(name: string): string {
  const parts = name.split(/[\s-]+/).filter((el) => /^[A-Za-zÄÖÜäöü0-9]/.test(el));
  return `${parts[0]?.[0] || ''}${parts.length > 1 ? parts[1][0] : ''}`.toUpperCase() || '?';
}

export function idsOf(list: unknown[] | undefined): string[] {
  return (list || []).map((el) => String((el as { _id?: unknown })?._id ?? el)).filter((id) => id && id !== 'undefined');
}

/** Names of populated institutions in a list (id → name) */
export function namesOf(list: unknown[] | undefined): Record<string, string> {
  const names: Record<string, string> = {};
  (list || []).forEach((el) => {
    const item = el as { _id?: unknown; name?: string };
    if (item && item._id && item.name) {
      names[String(item._id)] = item.name;
    }
  });
  return names;
}

export function coordinatesOf(employee: Employee | null): number[] | null {
  const coordinates = employee?.address?.location?.coordinates;
  return coordinates && coordinates.length === 2 && coordinates[0] !== 0 && coordinates[1] !== 0 ? coordinates : null;
}

/** Schools the applicant chose in the application (vanillaApplication.institutionsInRange) */
export function chosenIds(employee: Employee | null): string[] {
  return idsOf(employee?.vanillaApplication?.institutionsInRange as unknown[] | undefined);
}

const STATUS_TONES: Partial<Record<AcquisitionListStatusEnum, string>> = {
  [AcquisitionListStatusEnum.COMMITTED]: 'tone-good',
  [AcquisitionListStatusEnum.IN_PROGRESS]: 'tone-info',
  [AcquisitionListStatusEnum.POTENTIAL]: 'tone-teal',
  [AcquisitionListStatusEnum.AI_SUCCESS]: 'tone-teal',
  [AcquisitionListStatusEnum.CLOSED]: 'tone-bad',
};

function statusOf(institution: Institution): Reason | null {
  const status = institution.acquisitionStatus;
  const data = typeof status === 'number' ? AcquisitionListStatusEnum.Record[status] : undefined;
  if (!data) {
    return null;
  }
  return {text: data.text, icon: data.icon, tone: STATUS_TONES[status] || 'tone-muted', title: 'Status der Akquise'};
}

type Scored = { score: number; reasons: Reason[] };

function scoreAvailability(match: number[], schoolHasSlots: boolean, teacherHasSlots: boolean): Scored {
  if (match.length) {
    return {
      score: 30 + 5 * (match.length - 1),
      reasons: [{text: `passt ${match.map((day) => WORK_DAYS[day]).join(', ')}`, icon: 'mdi-calendar-check-outline', tone: 'tone-good',
        title: 'Verfügbarkeit der Lehrkraft überschneidet sich mit den Kurszeiten der Schule'}],
    };
  }
  if (!schoolHasSlots) {
    return {score: 0, reasons: [{text: 'keine Kurszeiten hinterlegt', icon: 'mdi-calendar-question', tone: 'tone-muted'}]};
  }
  return teacherHasSlots ? {score: -5, reasons: [{text: 'Zeiten passen nicht', icon: 'mdi-calendar-remove-outline', tone: 'tone-muted'}]}
    : {score: 0, reasons: []};
}

function scoreDistance(teacher: Employee | null, km: number | null): Scored {
  if (km === null) {
    return {score: -10, reasons: [{text: 'Entfernung unbekannt', icon: 'mdi-map-marker-question-outline', tone: 'tone-warn'}]};
  }
  let score = Math.max(0, (MAX_RADIUS_KM - km) / 2);
  const reasons: Reason[] = [];
  const travel = teacher?.maxTravelTime ? `, max. ${teacher.maxTravelTime} Min. Fahrzeit` : '';
  if (teacher?.maxDistance) {
    if (km <= teacher.maxDistance) {
      score += 15;
      reasons.push({text: 'im Wunschradius', icon: 'mdi-map-marker-check-outline', tone: 'tone-good', title: `möchte bis ${teacher.maxDistance} km fahren${travel}`});
    } else {
      score -= 10;
      reasons.push({text: `außerhalb Wunschradius (${teacher.maxDistance} km)`, icon: 'mdi-map-marker-alert-outline', tone: 'tone-warn',
        title: `möchte bis ${teacher.maxDistance} km fahren${travel}`});
    }
  }
  if (km > 10) {
    if (teacher?.hasCar && teacher.hasDriverLicense) {
      score += 5;
      reasons.push({text: 'mit Auto', icon: 'mdi-car-outline', tone: 'tone-info'});
    } else {
      score -= 8;
      reasons.push({text: 'ohne Auto', icon: 'mdi-car-off', tone: 'tone-warn', title: 'Mehr als 10 km Luftlinie ohne Auto bzw. Führerschein'});
    }
  }
  return {score, reasons};
}

function scoreNeed(institution: Institution, teachers: number | null | undefined): Scored {
  const reasons: Reason[] = [];
  let score = 0;
  if (teachers === 0) {
    score += 12;
    reasons.push({text: 'noch ohne Lehrkraft', icon: 'mdi-account-off-outline', tone: 'tone-good', title: 'Keine aktive Lehrkraft zugeteilt'});
  }
  if (institution.employeesWanted) {
    score += 10;
    reasons.push({text: 'Lehrkräfte gesucht', icon: 'mdi-account-search-outline', tone: 'tone-warn'});
  }
  const status = institution.acquisitionStatus;
  if (status === AcquisitionListStatusEnum.COMMITTED) {
    score += 8;
  } else if (status === AcquisitionListStatusEnum.IN_PROGRESS) {
    score += 4;
  }
  return {score, reasons};
}

function scoreRelation(institution: Institution, teacher: Employee | null, mode: PickerMode, chosen: boolean): Scored {
  const id = String(institution._id);
  const reasons: Reason[] = [];
  let score = 0;
  if (chosen) {
    score += 40;
    reasons.push({text: 'Von Bewerber:in gewählt', icon: 'mdi-hand-pointing-right', tone: 'tone-purple', title: 'In der Bewerbung als Wunschstandort angegeben'});
  }
  if (mode === 'assign' && containsId(teacher?.plannedInstitutions as unknown[] | undefined, id)) {
    score += 20;
    reasons.push({text: 'hier vorgemerkt', icon: 'mdi-bookmark-check-outline', tone: 'tone-purple'});
  }
  if (mode === 'plan' && containsId(teacher?.assignedInstitutions as unknown[] | undefined, id)) {
    reasons.push({text: 'schon zugeteilt', icon: 'mdi-account-check-outline', tone: 'tone-purple'});
  }
  if (!institution.isActive && institution.isActive !== undefined) {
    score -= 50;
    reasons.push({text: 'inaktiv', icon: 'mdi-domain-off', tone: 'tone-bad', title: 'Standort ist deaktiviert'});
  }
  return {score, reasons};
}

/** Builds the ranked row of one school (score + "Warum vorgeschlagen" tags) */
export function toSchoolRow(institution: Institution, teacher: Employee | null, mode: PickerMode, chosen: string[], teachers: number | null | undefined): SchoolRow {
  const id = String(institution._id);
  const distanceKm = airDistanceKm(teacher?.address, institution.address);
  const match = matchingDays(teacher?.availabilities, institution.availabilities);
  const isChosen = chosen.includes(id) || containsId(institution.applicants as unknown[] | undefined, String(teacher?._id));
  const parts = [
    scoreRelation(institution, teacher, mode, isChosen),
    scoreAvailability(match, courseSlots(institution.availabilities).length > 0, courseSlots(teacher?.availabilities).length > 0),
    scoreDistance(teacher, distanceKm),
    scoreNeed(institution, teachers),
  ];
  const name = institution.name || 'Unbekannt';
  const type = InstitutionTypesVue.find((el) => el.value === institution.type);
  return {
    id,
    institution,
    name,
    customId: institution.customId || '',
    initials: initialsOf(name),
    typeText: type ? type.text : '',
    city: institution.address?.city || '',
    distanceKm,
    matchDays: match,
    chosen: isChosen,
    pinned: isChosen || (mode === 'assign' && containsId(teacher?.plannedInstitutions as unknown[] | undefined, id)),
    inactive: institution.isActive === false,
    score: parts.reduce((sum, part) => sum + part.score, 0),
    reasons: parts.flatMap((part) => part.reasons),
    // dots show the school's course days: green = teacher fits, blue = school only, frame = teacher available but school has no slot
    days: dayStates(institution.availabilities, teacher?.availabilities, match),
    status: statusOf(institution),
  };
}

export function bySchoolScore(a: SchoolRow, b: SchoolRow): number {
  return b.score - a.score || (a.distanceKm ?? Number.MAX_SAFE_INTEGER) - (b.distanceKm ?? Number.MAX_SAFE_INTEGER);
}

export async function findInstitutions(filter: Record<string, unknown>, extra: Record<string, unknown> = {}): Promise<Institution[]> {
  const resp = await InstitutionerApi.find({filter, fields: INSTITUTION_FIELDS, ...extra} as any);
  return resp.institutions as unknown as Institution[];
}

/** Active teachers per school (without the edited employee), in a few chunked requests; resolves to null per id on error */
export async function countAssignedTeachers(ids: string[], ownId: string): Promise<Record<string, number | null>> {
  const counts: Record<string, number | null> = {};
  ids.forEach((id) => (counts[id] = 0));
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += COUNT_CHUNK) {
    chunks.push(ids.slice(i, i + COUNT_CHUNK));
  }
  await Promise.all(chunks.map((chunk) => EmployeeHandlerAPI.find({
    filter: {assignedInstitutions: chunk, isActive: true},
    fields: ['assignedInstitutions'],
    skipPagination: true,
  } as any).then((resp) => resp.employees.forEach((employee) => {
    if (String(employee._id) === ownId) {
      return;
    }
    idsOf(employee.assignedInstitutions as unknown[]).filter((id) => chunk.includes(id)).forEach((id) => (counts[id] = (counts[id] || 0) + 1));
  })).catch(() => chunk.forEach((id) => (counts[id] = null)))));
  return counts;
}

/** Ids arrive as strings from the API anyway, the typed ObjectId is only needed for the API signatures */
export function asObjectId(id: string): Types.ObjectId {
  return id as unknown as Types.ObjectId;
}

export type InstitutionChange = { added: string[]; removed: string[] };

/** Saves all changes of one employee; returns the number of failed requests */
export async function saveInstitutionAssignments(mode: PickerMode, employeeId: Types.ObjectId, change: InstitutionChange): Promise<number> {
  const results = await Promise.allSettled([
    ...change.added.map((id) => EmployeeHandlerAPI.addAssignment(employeeId, {type: mode, institutionId: asObjectId(id)})),
    ...change.removed.map((id) => EmployeeHandlerAPI.removeAssignment(employeeId, {type: mode, institutionId: asObjectId(id)})),
  ]);
  return results.filter((el) => el.status === 'rejected').length;
}
